import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { LearningMaterial } from '../LogistikProduct.js';
import { LogistikProductFilter } from '../LogistikProductFilter.js';

const subscribeLearningMaterials = registerSubscription({
  type: () => [LearningMaterial],
  operationName: 'subscribeLearningMaterials',
  options: {},
  params: [],
  resolve: () => {
    return (gqlFields: BaseQLSelectionSet<LearningMaterial>) => {
      return from(publishFilter(LearningMaterial._class)).pipe(
        switchMap(publishFilter => {
          return watchQuery<LearningMaterial>(
            LearningMaterial._collectionName,
            publishFilter
              ? {
                  $and: [
                    publishFilter,
                    { isPublished: { $ne: false } },
                    { _class: { $in: baseqlMetaData.entities.validSubclasses(LearningMaterial) } },
                  ],
                }
              : {
                  isPublished: { $ne: false },
                  _class: { $in: baseqlMetaData.entities.validSubclasses(LearningMaterial) },
                },
            {
              sort: { _id: -1 },
            },
          );
        }),
        catchError(err => {
          logger.error({ err }, 'Fehler in subscribeLearningMaterials');
          return of([] as LearningMaterial[]);
        }),
      );
    };
  },
});

const subscribeLearningMaterialsPaginated = registerSubscription({
  type: () => [LearningMaterial],
  operationName: 'subscribeLearningMaterialsPaginated',
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => LogistikProductFilter, name: 'filter' },
  ],
  resolve: (skip: number, limit: number, filter: LogistikProductFilter) => {
    return (gqlFields: BaseQLSelectionSet<LearningMaterial>) => {
      const selector = filter?.getMongoFilter() ?? {};
      return from(publishFilter(LearningMaterial._class)).pipe(
        switchMap(publishFilter => {
          return watchQuery(
            LearningMaterial._collectionName,
            publishFilter
              ? {
                  $and: [
                    publishFilter,
                    selector,
                    { isPublished: { $ne: false } },
                    { _class: { $in: baseqlMetaData.entities.validSubclasses(LearningMaterial) } },
                  ],
                }
              : {
                  selector,
                  isPublished: { $ne: false },
                  _class: { $in: baseqlMetaData.entities.validSubclasses(LearningMaterial) },
                },
            { sort: filter.getMongoOptions().sort },
          );
        }),
        catchError(err => {
          logger.error({ err }, 'Fehler in subscribeLearningMaterialsPaginated');
          return of([] as LearningMaterial[]);
        }),
      );
    };
  },
});

const subscribePublishedLearningMaterialsCount = registerSubscription({
  type: () => Int,
  operationName: 'subscribePublishedLearningMaterialsCount',
  options: {},
  params: [],
  resolve: () => {
    return () => {
      return watchQuery(
        LearningMaterial._collectionName,
        { _class: { $in: baseqlMetaData.entities.validSubclasses(LearningMaterial) }, isPublished: { $ne: false } },
        { projection: { _id: 1 } },
      ).pipe(map(learningMaterials => learningMaterials.length));
    };
  },
});

const getLearningMaterialSampleSet = registerQuery({
  type: () => [LearningMaterial],
  operationName: 'getLearningMaterialSampleSet',
  resolve: () => {
    return async (gqlFields: BaseQLSelectionSet<LearningMaterial>) => {
      const col = await getCollection<LearningMaterial>(LearningMaterial._class);
      const samples = await col
        .find<LearningMaterial>({ _class: LearningMaterial._class, availableQuantity: { $gt: 0 } })
        .toArray();
      return samples;
    };
  },
});

export namespace LearningMaterialAPI {
  export const subscribe = subscribeLearningMaterials;
  export const subscribePaginated = subscribeLearningMaterialsPaginated;
  export const count = subscribePublishedLearningMaterialsCount;
  export const getSampleSet = getLearningMaterialSampleSet;
}
